import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Boxes, Pencil, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { Organization, ProductBrand, ProductFamily } from '../../types/domain';

interface FamilyDraft {
  client: string;
  name: string;
  brand: string;
  is_active: boolean;
}

const emptyDraft: FamilyDraft = { client: '', name: '', brand: '', is_active: true };

export function ProductFamiliesPage() {
  const { accessToken, capabilities, isPlatformAdmin, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const canManage = capabilities.includes('products:manage');
  const [editing, setEditing] = useState<ProductFamily | 'new' | null>(null);
  const [draft, setDraft] = useState<FamilyDraft>(emptyDraft);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const params = new URLSearchParams({ ordering: 'name', page: String(page), page_size: '20' });
  if (debouncedSearch) params.set('search', debouncedSearch);
  if (selectedWorkspace?.type === 'client' && selectedWorkspace.organizationId) {
    params.set('client', selectedWorkspace.organizationId);
  }
  const families = useApiCollection<ProductFamily>(
    'product-families-screen',
    `/product-families/?${params.toString()}`,
    accessToken,
  );
  const organizations = useApiCollection<Organization>(
    'product-family-organizations',
    '/organizations/?page_size=100&ordering=name&type=client&status=active',
    accessToken,
    canManage,
  );
  const clientOptions = useMemo(() => (
    (organizations.data?.results ?? []).filter((organization) => (
      organization.type === 'client'
      && (isPlatformAdmin || organization.id === selectedWorkspace?.organizationId)
    ))
  ), [isPlatformAdmin, organizations.data?.results, selectedWorkspace?.organizationId]);
  const brandParams = new URLSearchParams({ ordering: 'name', page_size: '100' });
  if (draft.client) brandParams.set('client', draft.client);
  const brands = useApiCollection<ProductBrand>(
    'product-family-brands',
    `/product-brands/?${brandParams.toString()}`,
    accessToken,
    Boolean(editing && draft.client),
  );
  const brandOptions = (brands.data?.results ?? []).filter((brand) => (
    brand.client === draft.client && (brand.is_active || brand.id === draft.brand)
  ));

  const mutation = useMutation({
    mutationFn: () => apiRequest<ProductFamily>(
      editing === 'new' ? '/product-families/' : `/product-families/${editing?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editing === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify({
          ...(editing === 'new' ? { client: draft.client } : {}),
          name: draft.name,
          brand: draft.brand || null,
          is_active: draft.is_active,
        }),
      },
    ),
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyDraft);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-families-screen'] });
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-families'] });
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-brands-screen'] });
      await queryClient.invalidateQueries({ queryKey: ['collection', 'products'] });
    },
  });

  function beginCreate() {
    mutation.reset();
    setDraft({
      ...emptyDraft,
      client: selectedWorkspace?.type === 'client'
        ? selectedWorkspace.organizationId ?? ''
        : clientOptions[0]?.id ?? '',
    });
    setEditing('new');
  }

  function beginEdit(family: ProductFamily) {
    mutation.reset();
    setDraft({
      client: family.client,
      name: family.name,
      brand: family.brand ?? '',
      is_active: family.is_active,
    });
    setEditing(family);
  }

  function closeForm() {
    if (mutation.isPending) return;
    setEditing(null);
    setDraft(emptyDraft);
    mutation.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  const rows = families.data?.results ?? [];

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Catalogue</p>
          <h1>Product families</h1>
          <p>Group related sellable variants without merging their SKUs, prices, stock, or targets.</p>
        </div>
        <span className="record-count"><Boxes size={16} /> {families.data?.count ?? 0} families</span>
      </header>

      <div className="catalogue-list-toolbar">
        <label className="search-field"><Search aria-hidden="true" size={17} /><input aria-label="Search product families" onChange={(event) => setSearch(event.target.value)} placeholder="Search family, brand or client" type="search" value={search} /></label>
        {canManage && clientOptions.length > 0 ? <button className="button primary" onClick={beginCreate} type="button"><Plus size={17} /> Add family</button> : null}
      </div>

      <DataPanel loading={families.isLoading} error={families.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Family</th><th>Brand</th><th>Client</th><th>Variants</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{rows.map((family) => <tr key={family.id}>
              <td><strong>{family.name}</strong></td>
              <td>{family.brand_name || '—'}</td>
              <td>{family.client_name}</td>
              <td>{family.variant_count}</td>
              <td><StatusPill value={family.is_active ? 'active' : 'inactive'} /></td>
              <td><div className="table-actions">{canManage && (isPlatformAdmin || family.client === selectedWorkspace?.organizationId) ? <button className="text-button" onClick={() => beginEdit(family)} type="button"><Pencil size={14} /> Edit</button> : null}</div></td>
            </tr>)}</tbody>
          </table></div>
          <div className="attendance-pagination"><span>Page {page} · {families.data?.count ?? 0} records</span><div><button className="button secondary" disabled={!families.data?.previous} onClick={() => setPage((value) => Math.max(1, value - 1))} type="button">Previous</button><button className="button secondary" disabled={!families.data?.next} onClick={() => setPage((value) => value + 1)} type="button">Next</button></div></div>
        </section>
      </DataPanel>

      {editing ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }} role="presentation">
          <section aria-labelledby="product-family-form-title" aria-modal="true" className="confirmation-dialog catalogue-form-dialog" role="dialog">
            <button aria-label="Close family form" className="icon-button confirmation-close" disabled={mutation.isPending} onClick={closeForm} type="button"><X size={19} /></button>
            <div className="card-heading"><div><h2 id="product-family-form-title">{editing === 'new' ? 'Add product family' : `Edit ${editing.name}`}</h2><p>Families belong to one client and may be linked to one of its brands.</p></div></div>
            <form className="management-form" onSubmit={submit}>
              <div className="form-grid">
                <label>Client<select disabled={editing !== 'new'} required value={draft.client} onChange={(event) => setDraft({ ...draft, client: event.target.value, brand: '' })}><option value="">e.g. PepsiCo Pakistan</option>{clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
                <label>Family name<input placeholder="e.g. Pepsi Cola" required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
                <label>Brand<select value={draft.brand} onChange={(event) => setDraft({ ...draft, brand: event.target.value })}><option value="">No brand</option>{brandOptions.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}</select></label>
                <label className="checkbox-field"><input checked={draft.is_active} type="checkbox" onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} /> Available for new variants</label>
              </div>
              {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
              <div className="form-actions"><button className="button secondary" disabled={mutation.isPending} onClick={closeForm} type="button">Cancel</button><button className="button primary" disabled={!draft.client || mutation.isPending} type="submit">{mutation.isPending ? <InlineSpinner /> : null}{editing === 'new' ? 'Add family' : 'Save family'}</button></div>
            </form>
          </section>
        </div>
      ) : null}
    </>
  );
}
